<script setup lang="ts">
    import { computed, inject, onBeforeUnmount, onMounted, reactive, Ref, ref, watch } from 'vue';
    import axios from 'axios';
    import moment from 'moment';
    import { formatAmount, formatDate } from '@/utils';
    import { useToastStore } from '@/stores/toast.store';
    import { useConfirmationStore } from '@/stores/confirmation.store';
    import { useRoute } from 'vue-router';

    const route = useRoute();
    const toastStore = useToastStore();
    const startupInfos = reactive<{ salaires: any[] }>(route.meta.item as any);
    const confirmationStore = useConfirmationStore();
    const loading = ref(false);
    const collectivite = inject<Ref<any>>('collectivite');
    const itemsList = ref<any[]>([]);
    const filteredDatas = ref([] as Array<any>);
    const paginatedFilteredDatas = ref([] as Array<any>);
    const queryParams = reactive({
        pageNumber: 1
    });
    const pagination = reactive({
        total: 0,
        per_page: 10,
        last_page: 1
    });
    const filtersParams = reactive({
        name: '',
        statut_professionnel: null,
        salaires: null,
        quotite_cessible_min: null,
        quotite_cessible_max: null,
        age_min: null,
        age_max: null,
        sexe: null,
        fonction: null
    });
    const selectedItems = ref([] as any[]);
    const areAllItemsSelected = computed(() => {
        return paginatedFilteredDatas.value.length > 0 && selectedItems.value.filter(i => paginatedFilteredDatas.value.findIndex(p => p.id === i) !== -1).length === paginatedFilteredDatas.value.length;
    });
    const areAllItemsSelectedValidable = computed(() => {
        return selectedItems.value.length > 0 && itemsList.value.filter(i => selectedItems.value.includes(i.id) && i.statut_prospect == 0).length === selectedItems.value.length;
    });
    const showAdvancedFilters = ref(false);

    const columnsCatalog = [
        { key: 'photo', label: 'Photo' },
        { key: 'information', label: 'Information' },
        { key: 'date_naissance', label: 'Date de naissance', hidden: true },
        { key: 'age', label: 'Age', hidden: true },
        { key: 'sexe', label: 'Sexe', hidden: true },
        { key: 'retraite', label: 'Retraite', hidden: true },
        { key: 'salaire', label: 'Salaire', hidden: true },
        { key: 'utilisation', label: 'Utilisation', hidden: true },
        { key: 'statut', label: 'Statut', hidden: true },
    ];
    const visibleColumns = ref(columnsCatalog.filter(c => c.hidden === undefined || c.hidden === false).map(c => c.key));
    const showColumnsSelector = ref(false);
    const columnsSelectorRef = ref<HTMLElement | null>(null);

    const statsPersonnesActivesCount = computed(() => itemsList.value.filter(i => !i.retraite).length);
    const statsPersonnesActivesPercent = computed(() => {
        if (itemsList.value.length == 0) { return 0; }
        return Math.round((statsPersonnesActivesCount.value * 100) / itemsList.value.length);
    });

    const statsPersonnesRetraitesCount = computed(() => itemsList.value.filter(i => i.retraite).length);
    const statsPersonnesRetraitesPercent = computed(() => {
        if (itemsList.value.length == 0) { return 0; }
        return Math.round((statsPersonnesRetraitesCount.value * 100) / itemsList.value.length);
    });

    const statsPersonnesHommesCount = computed(() => itemsList.value.filter(i => i.sexe == 'M').length);
    const statsPersonnesHommesPercent = computed(() => {
        if (itemsList.value.length == 0) { return 0; }
        return Math.round((statsPersonnesHommesCount.value * 100) / itemsList.value.length);
    });

    const statsPersonnesFemmesCount = computed(() => itemsList.value.filter(i => i.sexe == 'F').length);
    const statsPersonnesFemmesPercent = computed(() => {
        if (itemsList.value.length == 0) { return 0; }
        return Math.round((statsPersonnesFemmesCount.value * 100) / itemsList.value.length);
    });

    onMounted(() => {
        fetchMembres();
        document.addEventListener('click', handleClickOutsideColumnsSelector);
    });

    watch(filtersParams, (oldValues, newValues) => {
        applyFilters();
        onPaginate(1);
    });

    onBeforeUnmount(() => {
        document.removeEventListener('click', handleClickOutsideColumnsSelector);
    });

    async function fetchMembres() {
        try {
            loading.value = true;
            const response = await axios.get(`/backoffice/collectivites/${collectivite?.value.id}/membres`);
            itemsList.value = response.data;
            applyFilters();
            onPaginate(1);
        } catch(error: any) {
            toastStore.error(error.message || 'Une erreur est survenue lors de la requête au serveur.');
        } finally {
            loading.value = false;
        }
    }

    function applyFilters() {
        let allDatas = itemsList.value;

        if (filtersParams.name) {
            allDatas = allDatas.filter(a => `${a.nom} ${a.prenom}`.toLowerCase().includes(filtersParams.name.toLowerCase()));
        }
        if (filtersParams.statut_professionnel) {
            if (filtersParams.statut_professionnel == '1') {
                allDatas = allDatas.filter(a => a.retraite === false);
            } else if (filtersParams.statut_professionnel == '2') {
                allDatas = allDatas.filter(a => a.retraite === true);
            }
        }
        if (filtersParams.sexe) {
            allDatas = allDatas.filter(a => a.sexe == filtersParams.sexe);
        }
        if (filtersParams.quotite_cessible_min != null && !isNaN(filtersParams.quotite_cessible_min)) {
            const quotite_cessible_min = parseFloat(filtersParams.quotite_cessible_min);
            allDatas = allDatas.filter(a => a.quotite_cessible_montant && !isNaN(a.quotite_cessible_montant) && a.quotite_cessible_montant >= quotite_cessible_min);
        }
        if (filtersParams.quotite_cessible_max != null && !isNaN(filtersParams.quotite_cessible_max)) {
            const quotite_cessible_max = parseFloat(filtersParams.quotite_cessible_max);
            allDatas = allDatas.filter(a => a.quotite_cessible_montant && !isNaN(a.quotite_cessible_montant) && a.quotite_cessible_montant <= quotite_cessible_max);
        }
        if (filtersParams.age_min) {
            const age_min = moment(filtersParams.age_min);
            if (age_min.isValid()) {
                allDatas = allDatas.filter(a => {
                    const date_naissance = moment(a.date_naissance);
                    return date_naissance.isValid() && date_naissance.isSameOrAfter(age_min);
                })
            }
        }
        if (filtersParams.age_max) {
            const age_max = moment(filtersParams.age_max);
            if (age_max.isValid()) {
                allDatas = allDatas.filter(a => {
                    const date_naissance = moment(a.date_naissance);
                    return date_naissance.isValid() && date_naissance.isSameOrBefore(age_max);
                })
            }
        }
        if (filtersParams.salaires != null) {
            const matchSalaire = startupInfos.salaires[filtersParams.salaires];
            if (matchSalaire) {
                allDatas = allDatas.filter(a => !isNaN(a.salaire) && parseFloat(a.salaire) >= parseFloat(matchSalaire.salaire_min) && parseFloat(a.salaire) <= parseFloat(matchSalaire.salaire_max));
            }
        }
        
        filteredDatas.value = allDatas;
    }

    function onPaginate(page: number) {
        queryParams.pageNumber = page;
        pagination.total = filteredDatas.value.length;
        pagination.last_page = Math.ceil(pagination.total / pagination.per_page);
        const start = (page - 1) * pagination.per_page;
        paginatedFilteredDatas.value =  filteredDatas.value.slice(start, start + pagination.per_page);
    }

    function handleCheckAll(_event: any) {
        const checked = _event.target.checked;
        if (checked) {
            selectedItems.value = [...selectedItems.value.filter(s => itemsList.value.findIndex(p => p.id === s) === -1), ...itemsList.value.map(i => i.id)];
        } else {
            selectedItems.value = [];
        }
    }

    function toggleAdvancedFilters() {
        showAdvancedFilters.value = !showAdvancedFilters.value;
    }

    function isColumnVisible(key: string) {
        return visibleColumns.value.includes(key);
    }

    function toggleColumn(key: string) {
        const index = visibleColumns.value.indexOf(key);
        if (index === -1) {
            visibleColumns.value.push(key);
        } else if (visibleColumns.value.length > 1) {
            visibleColumns.value.splice(index, 1);
        }
    }

    function toggleColumnsSelector() {
        showColumnsSelector.value = !showColumnsSelector.value;
    }

    function handleClickOutsideColumnsSelector(event: MouseEvent) {
        if (columnsSelectorRef.value && !columnsSelectorRef.value.contains(event.target as Node)) {
            showColumnsSelector.value = false;
        }
    }
</script>
<template>
    <section class="s16_mobile px-4">
        <div class="widget px-7 py-7">
            
            <div class="s7_mobile container-full-2">
                <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                    <img src="/icons/icon-info-perso.svg" class="size-l" alt="" />
                    Membres ({{ filteredDatas.length }})
                </h2>
            </div> 
            <div class="s13 flex justify-content-right">
                <custom-button type="button" :classesList="'btn light'" @click="fetchMembres">
                    <img src="/icons/icon-list.svg" class="btn-icon" alt="" />
                    <span>Actualiser</span>
                </custom-button>
                <button class="btn normal white2 s3_mobile ml-4 justify-content-center btn-filtre btn-filter" @click="toggleAdvancedFilters">
                    <img src="/icons/icon-filter.svg" class="btn-icon" alt="" />
                    <span>Options</span>
                </button>
                <div class="columns-selector s3_mobile ml-4 " ref="columnsSelectorRef">
                    <button type="button" class="btn normal justify-content-center white2 btn-filtre btn-filter" @click="toggleColumnsSelector">
                        <img src="/icons/icon-list.svg" class="btn-icon" alt="" />
                        <span>Colonnes</span>
                    </button>
                    <div class="columns-selector-panel" v-if="showColumnsSelector">
                        <label class="columns-selector-item" v-for="col in columnsCatalog" :key="col.key">
                            <input type="checkbox" :checked="isColumnVisible(col.key)" @change="toggleColumn(col.key)" />
                            <span>{{ col.label }}</span>
                        </label>
                    </div>
                </div>
            </div>
            <div class="clear-both"></div>

            <div class="s20 items-center gap-6 px-0 pb-6 collapse-content active">
                <!-- Filtre - ajouter -->
                <div class="custom-filter-set-area my-5" v-if="showAdvancedFilters">
                    <div class="grid-filter6 mt-4 mb-0">
                        <div class="form-field-floating reactive sm">
                            <input type="text" class="form-input" v-model="filtersParams.name" name="name" id="name" placeholder="">
                            <label for="name">Nom, prénoms</label>
                        </div>
                        <div class="form-field-floating sm">
                            <select class="form-input-select" v-model="filtersParams.statut_professionnel" name="statut_professionnel" id="statut_professionnel">
                                <option :value="null">Tous les statuts</option>
                                <option value="1">Actifs</option>
                                <option value="2">Retraités</option>
                            </select>
                            <label for="statut_professionnel">Statut <b class="color-danger">*</b></label>
                        </div>
                        <div class="form-field-floating sm">
                            <select class="form-input-select" v-model="filtersParams.salaires" name="salaires" id="salaires">
                                <option :value="null">Tous les salaires</option>
                                <option :value="index" v-for="(salaire, index) of startupInfos.salaires" :key="index">{{ formatAmount(salaire.salaire_min) + ' - ' + formatAmount(salaire.salaire_max) }}</option>
                            </select>
                            <label for="salaires">Salaires <b class="color-danger">*</b></label>
                        </div>
                        <div class="form-field-floating reactive sm">
                            <input type="number" class="form-input" v-model="filtersParams.quotite_cessible_min" name="quotite_cessible_min" id="quotite_cessible_min" placeholder="">
                            <label for="quotite_cessible_min">Quotité cessible &gt;=</label>
                        </div>
                        <div class="form-field-floating reactive sm">
                            <input type="number" class="form-input" v-model="filtersParams.quotite_cessible_max" name="quotite_cessible_max" id="quotite_cessible_max" placeholder="">
                            <label for="quotite_cessible_max">Quotité cessible &gt;=</label>
                        </div>
                        <div class="form-field-floating reactive sm">
                            <input type="date" class="form-input" v-model="filtersParams.age_min" name="age_min" id="age_min" placeholder="">
                            <label for="age_min">Age &gt;=</label>
                        </div>
                        <div class="form-field-floating reactive sm">
                            <input type="date" class="form-input" v-model="filtersParams.age_max" name="age_max" id="age_max" placeholder="">
                            <label for="age_max">Age &lt;=</label>
                        </div>
                        <div class="form-field-floating sm">
                            <select class="form-input-select" v-model="filtersParams.sexe" name="sexe" id="sexe">
                                <option :value="null">Tous les sexes</option>
                                <option value="M">Masculin</option>
                                <option value="F">Féminin</option>
                            </select>
                            <label for="sexe">Sexe <b class="color-danger">*</b></label>
                        </div>
                        <div class="form-field-floating sm">
                            <select class="form-input-select" v-model="filtersParams.fonction" name="fonction" id="fonction">
                                <option :value="null">Toutes les fonctions</option>
                            </select>
                            <label for="fonction">Fonction <b class="color-danger">*</b></label>
                        </div>
                    </div>
                </div>
                <!---->
                <table class="table separator skin3 selection rounded"><!-- selection skin1 skin2 (bg1 bg2 bg3)  (skin3 rounded)  sm-->
                    <thead>
                        <tr>
                            <th>
                                <label class="btn-circle" for="check-list1">
                                    <input type="checkbox" id="check_all" :checked="areAllItemsSelected" @change="handleCheckAll" />
                                </label>
                            </th>
                            <th v-if="isColumnVisible('photo')"></th>
                            <th v-if="isColumnVisible('information')"><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Information</th>
                            <th v-if="isColumnVisible('date_naissance')"><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Date de naissance</th>
                            <th v-if="isColumnVisible('age')"><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Age</th>
                            <th v-if="isColumnVisible('sexe')"><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Sexe</th>
                            <th v-if="isColumnVisible('retraite')"><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Retraite</th>
                            <th v-if="isColumnVisible('salaire')"><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Salaire</th>
                            <th v-if="isColumnVisible('utilisation')"><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Utilisation</th>
                            <th v-if="isColumnVisible('statut')"><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Statut</th>
                            <th v-if="isColumnVisible('contact')"><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Contact</th>
                            <th class="flex-right">
                                
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="loading">
                            <tr>
                                <td colspan="12" class="text-center py-5">
                                    <div style="display: flex; justify-content: center" v-if="loading">
                                        <div class="loader dark"></div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                        <template v-else-if="paginatedFilteredDatas.length === 0">
                            <tr>
                                <td colspan="12" class="text-center py-5">
                                    Aucun membre trouvé
                                </td>
                            </tr>
                        </template>
                        <template v-else>
                            <tr class="text-grey-700 over-effect" v-for="(item, index) in paginatedFilteredDatas" :key="'item_' + index"><!-- over-effect active-->
                                <td class="selection-item">
                                    <label class="btn-circle" :for="'item_check_' + index">
                                        <input type="checkbox" name="selectedItems[]" :id="'item_check_' + index" :value="item.id" v-model="selectedItems" />
                                    </label>
                                </td>
                                <td class="flex" v-if="isColumnVisible('photo')">
                                    <div class="flex align-items-center bg-white h-100p">
                                        <div class="square-xl rounded-15" style="background-color: rgb(var(--color-warning-rgb), 0.2);"><img :src="!!item.photo_url ? item.photo_url : '/assets/users_avatar/600w/user.jpg'" width="100%" /></div>
                                    </div>
                                </td>
                                <td v-if="isColumnVisible('information')">
                                    <p class="text-xl font-semibold mb-2">
                                        {{ item.nom + ' ' + item.prenom }} &nbsp; 
                                        <span v-if="!item.email_verified_at" class="tag-state warning stroke">En cours</span>
                                        <span v-if="item.email_verified_at" class="tag-state success stroke">Vérifié</span>
                                        <span v-if="item.actif == 1" class="tag-state notif stroke">Actif</span>
                                        <span v-if="item.actif == 0" class="tag-state danger stroke">Inactif</span>
                                    </p>
                                    <p class="text-sm text-grey-600">{{ item.email }}</p>
                                    <p class="text-sm text-grey-600">{{ item.telephone }}</p>
                                </td>
                                <td class="text-xl font-semibold" v-if="isColumnVisible('date_naissance')">
                                    {{ formatDate(item.date_naissance, 'DD/MM/YYYY') }}
                                </td>
                                <td class="text-xl font-semibold" v-if="isColumnVisible('age')">
                                    {{ item.age }}
                                </td>
                                <td class="text-xl font-semibold" v-if="isColumnVisible('sexe')">
                                    {{ item.sexe }}
                                </td>
                                <td class="text-xl font-semibold" v-if="isColumnVisible('retraite')">
                                    {{ item.retraite ? 'Oui' : 'Non' }}
                                </td>
                                <td class="text-xl font-semibold" v-if="isColumnVisible('salaire')">
                                    {{ formatAmount(item.salaire) }} F
                                </td>
                                <td class="text-3xl" v-if="isColumnVisible('utilisation')">
                                    <template v-if="item.quotite_cessible_pourcent">
                                        {{ item.quotite_cessible_pourcent }}%
                                    </template>
                                    <template v-else>-</template>
                                    <p class="mt-1 text-sm">
                                        <template v-if="item.quotite_cessible_montant">
                                            {{ new Intl.NumberFormat('fr-FR').format(item.quotite_cessible_montant) }} F
                                        </template>
                                        <template v-else>-</template>
                                    </p>
                                </td>
                                <td v-if="isColumnVisible('statut')">
                                    <p class="text-xl font-semibold mb-2">
                                        <span class="tag-state warning stroke" v-if="item.statut_prospect == 0">En attente</span>
                                        <span class="tag-state success stroke" v-if="item.statut_prospect == 1">Validé</span>
                                        <span class="tag-state danger stroke" v-if="item.statut_prospect == -1">Rejeté</span>
                                    </p>
                                </td>
                                <td>
                                    <div class="flex-right gap-4">
                                        <router-link :to="'/app/users/form/' + item.id">
                                            <button class="btn tips">
                                                <img src="/icons/icon-edit.svg" class="btn-icon" alt="" />
                                                <span>Editer</span>
                                            </button>
                                        </router-link>
                                        <custom-button :classesList="'btn-ultra color2 tips'" v-if="item.deleted_at == null && item.actif == 1">
                                            <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                                            <span>Actif</span>
                                        </custom-button>
                                        <custom-button :classesList="'btn-ultra color2 tips'" v-if="item.deleted_at == null && item.actif == 0">
                                            <img src="/icons/icon-inactif.svg" class="btn-icon" alt="" />
                                            <span>Inactif</span>
                                        </custom-button>
                                        <router-link :to="'/app/users/' + item.id + '/details'">
                                            <button class="btn white2 active">
                                                <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                                <span>Gérer</span>
                                            </button>
                                        </router-link>
                                        <router-link :to="'/app/users/' + item.id + '/carte'">
                                            <button class="btn white2 active">
                                                <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                                <span>Carte</span>
                                            </button>
                                        </router-link>
                                        <router-link :to="'/app/users/' + item.id + '/utilisation'">
                                            <button class="btn white2 active">
                                                <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                                <span>Utilisation</span>
                                            </button>
                                        </router-link>
                                        <router-link :to="'/app/users/' + item.id + '/plan'">
                                            <button class="btn white2 active">
                                                <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                                <span>Plan</span>
                                            </button>
                                        </router-link>
                                        <router-link :to="'/app/users/' + item.id + '/revolving'">
                                            <button class="btn white2 active">
                                                <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                                <span>Revolving</span>
                                            </button>
                                        </router-link>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
                <!--Pagination-->
                <custom-pagination v-model="queryParams.pageNumber" :total="pagination.total" :per-page="pagination.per_page" :last_page="pagination.last_page" @update:modelValue="onPaginate" />
                <!---->
            </div>

            <div class="clear-both"></div>
        </div>
    </section>

    <!-- Stat - ajouter -->
    <div class="s4 pl-5 pr-7">
        <!--stat circle big-->
        <div class="w-100 margin-auto mb-5 widget flex-center rounded-15">
            <div class="single-chart s6">
                <svg viewBox="0 0 36 36" class="circular-chart statorange">
                <path class="circle-bg"
                    d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831 
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path class="circle"
                    :stroke-dasharray="statsPersonnesActivesPercent + ', 100'"
                    d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831 
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <text x="18" y="20.35" class="percentage font-bold">{{ statsPersonnesActivesPercent }}%</text>
                </svg>
            </div>
            <div class="s14 px-4">
                <h3 class="font-medium">{{ statsPersonnesActivesCount }}</h3>
                <h6 class="font-medium">Personnes actives</h6>
            </div>
        </div>
        <!---->
        <!--stat circle big-->
        <div class="w-100 margin-auto mb-5 widget flex-center rounded-15">
            <div class="single-chart s6">
                <svg viewBox="0 0 36 36" class="circular-chart statorange">
                <path class="circle-bg"
                    d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831 
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path class="circle"
                    :stroke-dasharray=" statsPersonnesRetraitesPercent + ', 100'"
                    d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831 
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <text x="18" y="20.35" class="percentage font-bold">{{ statsPersonnesRetraitesPercent }}%</text>
                </svg>
            </div>
            <div class="s14 px-4">
                <h3 class="font-medium">{{ statsPersonnesRetraitesCount }}</h3>
                <h6 class="font-medium">Personnes retraités</h6>
            </div>
        </div>
        <!---->
        <!--stat circle big-->
        <div class="w-100 margin-auto mb-5 widget flex-center rounded-15">
            <div class="single-chart s6">
                <svg viewBox="0 0 36 36" class="circular-chart statorange">
                <path class="circle-bg"
                    d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831 
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path class="circle"
                    :stroke-dasharray="statsPersonnesHommesPercent + ', 100'"
                    d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831 
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <text x="18" y="20.35" class="percentage font-bold">{{ statsPersonnesHommesPercent }}%</text>
                </svg>
            </div>
            <div class="s14 px-4">
                <h3 class="font-medium">{{ statsPersonnesHommesCount }}</h3>
                <h6 class="font-medium">Hommes</h6>
            </div>
        </div>
        <!---->
        <!--stat circle big-->
        <div class="w-100 margin-auto mb-5 widget flex-center rounded-15">
            <div class="single-chart s6">
                <svg viewBox="0 0 36 36" class="circular-chart statorange">
                <path class="circle-bg"
                    d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831 
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path class="circle"
                    :stroke-dasharray="statsPersonnesFemmesPercent + ', 100'"
                    d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831 
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <text x="18" y="20.35" class="percentage font-bold">{{ statsPersonnesFemmesPercent }}%</text>
                </svg>
            </div>
            <div class="s14 px-4">
                <h3 class="font-medium">{{ statsPersonnesFemmesCount }}</h3>
                <h6 class="font-medium">Femmes</h6>
            </div>
        </div>
        <!---->
    </div>
    <!---->
</template>
<style scoped>
    .columns-selector {
        position: relative;
        display: inline-block;
    }
    .columns-selector-panel {
        position: absolute;
        top: calc(100% + 6px);
        right: 0;
        z-index: 50;
        min-width: 200px;
        background: #fff;
        border-radius: 10px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        padding: 10px 0;
    }
    .columns-selector-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        cursor: pointer;
        white-space: nowrap;
    }
    .columns-selector-item:hover {
        background: rgba(var(--primary-color-rgb), 0.08);
    }
</style>